import type { ComponentPropsWithoutRef, Ref } from 'react';
import {
  Avatar,
  AvatarFallback,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { getInitials } from '@/shared/utils/getInitials';

export interface SidebarUserTriggerProps extends ComponentPropsWithoutRef<'button'> {
  /** Full user name — used for initials and tooltip. */
  name: string;
  /** React 19 ref-as-prop: forwarded to the underlying `<button>`. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The user avatar button at the bottom of the sidebar.
 *
 * Figma specs:
 * - 40×40, round, bg accent (#E7F3FD), initials
 * - Wrapped in Tooltip showing user name
 */
export function SidebarUserTrigger({ name, className, ref, ...props }: SidebarUserTriggerProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={ref}
          type="button"
          className={[
            'flex cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 outline-none focus-visible:focus-ring-sidebar',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...props}
        >
          <Avatar size="default" roundness="full">
            <AvatarFallback size="default" roundness="full" className="bg-accent text-primary">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{name}</TooltipContent>
    </Tooltip>
  );
}

SidebarUserTrigger.displayName = 'SidebarUserTrigger';
